// CreateForecastWizard.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Autocomplete,
  FormControlLabel,
  Checkbox
} from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ImageIcon from '@mui/icons-material/Image';
import { sanitizeText, sanitizeURL, isSafeImageURL, extFromMime } from '../lib/sanitize';

import { toast } from 'react-toastify';
import axios from 'axios';

import { collection, addDoc, getDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { sanitizeForecastData } from './utils/sanitizeForecastData';


/* ===== Black Glass tokens  ===== */
const R12 = '12px';
const GLASS = {
  panel:  'linear-gradient(135deg, rgba(18,22,28,.92) 0%, rgba(10,13,17,.92) 100%)',
  surface:'linear-gradient(180deg, rgba(12,16,22,.92) 0%, rgba(8,11,17,.92) 60%)',
  border: '1px solid rgba(255,255,255,.10)',
  borderStrong: '1px solid rgba(255,255,255,.16)',
  shadow: '0 10px 32px rgba(2,8,23,.45)',
  text: '#e5e7eb',
  subtext: '#9ca3af',
  green: '#34d399',
  red: '#f87171',
};
const DARK = '#0b0f15'; 






/* ---------- constants ---------- */
const MIN_DESC = 10;
const EMPTY_IMG =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
const DRAFT_PREFIX = 'forecastDraft_';

/* ---------- helpers ---------- */
const apiURL = (pathWithQuery) => {
  const path = pathWithQuery.startsWith('/') ? pathWithQuery : `/${pathWithQuery}`;
  return new URL(path, window.location.origin).toString();
};

const isRangeWithDollar = (v) => /^\d+(\.\d+)?-\d+(\.\d+)?\$$/.test(v || '');



const PLATFORM_DISCLAIMER =
  'Forecasts are opinions, not financial advice. Markets are volatile and there are no guarantees. Do your own research (DYOR).';

function hasRiskDisclosure(txt) {
  const re =
    /\b(not\s+(financial|investment)\s+advice|dyor|no\s+guarantees?|high\s+risk|market\s+volatility|speculative|may\s+be\s+wrong)\b/i;
  return re.test(String(txt || ''));
}

// const isSafeImageURL = (url) => {
//   const u = sanitizeURL(url);
//   if (!u) return false;
//   const hasImageExt = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(u);
//   const looksLikeResource =
//     /^https?:\/\/.+\/[^/?#]+(\?.*)?(#.*)?$/i.test(u) || hasImageExt;
//   return looksLikeResource;
// };

// date utils
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const toISODate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};
const addMonths = (date, n) => {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + n);
  if (d.getDate() < day) d.setDate(0);
  return d;
};
const addYears = (date, n) => {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + n);
  return d;
};

// map old range-like presets to valid durations
const migratePreset = (p) => {
  switch (p) {
    case '1-7 days': return '7 days';
    case '7-15 days': return '15 days';
    case '15-30 days': return '30 days';
    case '1-3 months': return '3 months';
    case '3-6 months': return '6 months';
    default: return p;
  }
};

// convert a valid duration label to ISO date from now
const durationToISO = (label) => {
  const now = new Date();
  const m = /^\s*(\d+)\s+(day|days|month|months|year|years)\s*$/i.exec(label || '');
  if (!m) return null;
  const value = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  if (unit.startsWith('day')) return toISODate(addDays(now, value));
  if (unit.startsWith('month')) return toISODate(addMonths(now, value));
  if (unit.startsWith('year')) return toISODate(addYears(now, value));
  return null;
};

/* ---------- small UI parts (цвета → GLASS) ---------- */
function FieldHint({ text }) {
  return (
    <Tooltip title={text} placement="right" arrow>
      <IconButton size="small" sx={{ ml: 0.5, color: GLASS.subtext }}>
        <InfoIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function DescField({ label, name, value, onChange, hint, errorText }) {
  const cnt = (value || '').length;
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="subtitle2" sx={{ color: GLASS.text }}>{label}</Typography>
        <FieldHint text={hint} />
        <Typography variant="caption" sx={{ ml: 'auto', color: GLASS.subtext }}>
          {cnt}/{Math.max(MIN_DESC, 200)}
        </Typography>
      </Stack>
      <TextField
        fullWidth
        multiline
        rows={3}
        name={name}
        value={value}
        onChange={onChange}
        error={Boolean(errorText)}
        helperText={errorText || `Minimum ${MIN_DESC} characters`}
        sx={{
          mt: 0.5,
          '& .MuiInputBase-root': {
            color: GLASS.text,
            background: 'rgba(255,255,255,.02)',
          },
          '& .MuiInputBase-input': { color: GLASS.text },
          '& .MuiFormHelperText-root': { color: GLASS.subtext },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.20)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.35)' },
        }}
      />
    </Box>
  );
}

/* ---------- main ---------- */
export default function CreateForecastForm({ userId, onCreated, editData, onCloseEdit }) {
  // reference data (valid moderation-friendly presets)
  const assetsList = useMemo(
    () => ['BTC', 'ETH', 'USDT', 'BNB', 'SOL', 'ADA', 'XRP'],
    []
  );
  const predictionTypes = useMemo(
    () => [
      { v: 'pump', label: 'PUMP' },
      { v: 'dump', label: 'DUMP' },
      { v: 'flat', label: 'FLAT' },
      { v: 'rugPull', label: 'RUGPULL' }
    ],
    []
  );
  const duePresets = useMemo(
    () => ['7 days', '15 days', '30 days', '3 months', '6 months', '1 year'],
    []
  );

  // form state
  const [formData, setFormData] = useState({
    title: '',
    predictionType: 'pump',
    purchasePrice: '',
    targetPrice: '',
    dueType: 'preset',
    duePreset: '7 days',
    dueCustom: '',
    description: {
      technicalAnalysis: '',
      fundamental: '',
      news: '',
      conclusion: ''
    },
    assets: [],
    images: [],
    imagePaths: [],
    newsLinks: ['', ''],
    tipsEnabled: false
  });

  // NEW: risk disclaimer toggle
  const [riskDisclaimer, setRiskDisclaimer] = useState(true);

  const [imageFiles, setImageFiles] = useState([null, null]);
  const [previewUrls, setPreviewUrls] = useState(['', '']);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // wizard
  const steps = ['Basics', 'Pricing', 'Deadline', 'Description', 'Media & Links', 'Review'];
  const [activeStep, setActiveStep] = useState(0);

  // previews
  useEffect(() => {
    const urls = imageFiles.map((f) => (f ? URL.createObjectURL(f) : ''));
    setPreviewUrls(urls);
    return () => urls.forEach((u) => u && URL.revokeObjectURL(u));
  }, [imageFiles]);

  // load edit or draft (with preset migration)
  useEffect(() => {
    if (editData) {
      const migrated = migratePreset(editData.duePreset || '');
      setFormData({
        title: editData.title || '',
        predictionType: editData.predictionType || 'pump',
        purchasePrice: editData.purchasePrice || '',
        targetPrice: editData.targetPrice || '',
        dueType: editData.due && /^\d{4}-\d{2}-\d{2}$/.test(editData.due) ? 'custom' : 'preset',
        duePreset: duePresets.includes(migrated) ? migrated : '7 days',
        dueCustom: editData.due && /^\d{4}-\d{2}-\d{2}$/.test(editData.due) ? editData.due : '',
        description: {
          technicalAnalysis: editData.description?.technicalAnalysis || '',
          fundamental: editData.description?.fundamental || '',
          news: editData.description?.news || '',
          conclusion: editData.description?.conclusion || ''
        },
        assets: editData.assets || [],
        images: editData.images || [],
        imagePaths: editData.imagePaths || [],
        newsLinks: editData.newsLinks || ['', ''],
        tipsEnabled: !!editData.tipsEnabled
      });
      setImageFiles([null, null]);
      setPreviewUrls(['', '']);
      return;
    }
    // load draft
    const key = `${DRAFT_PREFIX}${userId || 'anon'}`;
    const raw = localStorage.getItem(key);
    if (raw) {
      try {
        const draft = JSON.parse(raw);
        const migratedPreset = migratePreset(draft.duePreset || '');
        setFormData((p) => ({
          ...p,
          ...draft,
          duePreset: duePresets.includes(migratedPreset) ? migratedPreset : '7 days'
        }));
      } catch {}
    }
  }, [editData, userId, duePresets]);

  // autosave
  useEffect(() => {
    const key = `${DRAFT_PREFIX}${userId || 'anon'}`;
    localStorage.setItem(key, JSON.stringify(formData));
  }, [formData, userId]);

  const resetDraft = (showToast = true) => {
    const key = `${DRAFT_PREFIX}${userId || 'anon'}`;
    localStorage.removeItem(key);
    if (showToast) toast.info('Draft cleared');
  };

  const setFD = (patch) => setFormData((p) => ({ ...p, ...patch }));

  // per-step validation
  const validateStep = (stepIndex) => {
    const fe = {};
    if (stepIndex === 0) {
      if (!formData.title?.trim()) fe.title = 'Required';
      if (!formData.assets?.length) fe.assets = 'Select at least one asset';
    }
    if (stepIndex === 1) {
      if (['pump', 'dump', 'flat'].includes(formData.predictionType)) {
        if (!isRangeWithDollar(formData.purchasePrice)) fe.purchasePrice = 'Use format "100-120$"';
        if (!isRangeWithDollar(formData.targetPrice)) fe.targetPrice = 'Use format "100-1500$"';
      }
    }
    if (stepIndex === 2) {
      const due = formData.dueType === 'preset' ? formData.duePreset : formData.dueCustom;
      if (!due) fe.due = 'Select a deadline';
      if (formData.dueType === 'custom') {
        const dt = new Date(formData.dueCustom);
        if (!(dt instanceof Date) || isNaN(dt.getTime()) || dt <= new Date()) fe.due = 'Pick a future date';
      } else {
        if (!duePresets.includes(formData.duePreset)) fe.due = 'Choose one of the presets';
      }
    }
    if (stepIndex === 3) {
      const d = formData.description || {};
      if ((d.technicalAnalysis || '').trim().length < MIN_DESC) fe['d.technicalAnalysis'] = 'Too short';
      if ((d.fundamental || '').trim().length < MIN_DESC) fe['d.fundamental'] = 'Too short';
      if ((d.news || '').trim().length < MIN_DESC) fe['d.news'] = 'Too short';
      if ((d.conclusion || '').trim().length < MIN_DESC) fe['d.conclusion'] = 'Too short';
    }
    if (stepIndex === 4) {
      const badLink = (formData.newsLinks || []).find((l) => l && !/^https?:\/\/.+/i.test(l));
      if (badLink) fe.newsLinks = 'Invalid URL';
    }
    setFieldErrors(fe);
    return Object.keys(fe).length === 0;
  };

  const next = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => setActiveStep((s) => Math.max(s - 1, 0));

  // AI suggest (contextual)
  const applyAIStep = async () => {
    setLoading(true);
    try {
      const payloadForStep = (() => {
        const {
          title, assets, predictionType, purchasePrice, targetPrice,
          dueType, duePreset, dueCustom, description, newsLinks
        } = formData;
        switch (activeStep) {
          case 0: return { title, assets, predictionType };
          case 1: return { predictionType, purchasePrice, targetPrice };
          case 2: return { dueType, duePreset, dueCustom };
          case 3: return { description };
          case 4: return { newsLinks };
          default: return { title, assets, predictionType, purchasePrice, targetPrice, dueType, duePreset, dueCustom, description, newsLinks };
        }
      })();

      const prompt = `
You are an AI assistant for a blockchain prediction platform.
Improve or complete only the provided fields. Return JSON with the same structure as input.
For deadlines, prefer single durations like "7 days", "30 days", "3 months", "6 months", "1 year" or an ISO date (YYYY-MM-DD).

Input:
${JSON.stringify(payloadForStep, null, 2)}
`.trim();

      const { data } = await axios.post(
        'https://europe-west3-starwa-94ac5.cloudfunctions.net/callOpenAI',
        {
          model: 'gpt-3.5-turbo',
          messages: [
            { role: 'system', content: 'You are an AI assistant for forecast creation.' },
            { role: 'user', content: prompt }
          ],
          max_tokens: 400,
          temperature: 0.6
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const ai = JSON.parse(data.choices[0]?.message?.content || '{}');

      if (activeStep === 0) {
        setFD({
          title: ai.title ?? formData.title,
          assets: Array.isArray(ai.assets) && ai.assets.length ? ai.assets : formData.assets,
          predictionType: ai.predictionType ?? formData.predictionType
        });
      } else if (activeStep === 1) {
        setFD({
          purchasePrice: ai.purchasePrice ?? formData.purchasePrice,
          targetPrice: ai.targetPrice ?? formData.targetPrice
        });
      } else if (activeStep === 2) {
        let nextPreset = ai.duePreset ?? formData.duePreset;
        nextPreset = migratePreset(nextPreset);
        setFD({
          dueType: ai.dueType ?? formData.dueType,
          duePreset: duePresets.includes(nextPreset) ? nextPreset : formData.duePreset,
          dueCustom: ai.dueCustom ?? formData.dueCustom
        });
      } else if (activeStep === 3) {
        setFD({
          description: {
            technicalAnalysis: ai.description?.technicalAnalysis ?? formData.description.technicalAnalysis,
            fundamental: ai.description?.fundamental ?? formData.description.fundamental,
            news: ai.description?.news ?? formData.description.news,
            conclusion: ai.description?.conclusion ?? formData.description.conclusion
          }
        });
      } else if (activeStep === 4) {
        setFD({
          newsLinks: Array.isArray(ai.newsLinks) ? ai.newsLinks.slice(0, 2) : formData.newsLinks
        });
      }

      toast.success('AI suggestions applied');
    } catch (e) {
      console.error(e);
      toast.error('AI suggest failed');
    } finally {
      setLoading(false);
    }
  };

  // images
  const handleImagePick = (e, idx) => {
  const file = e.target.files?.[0];
  if (!file) return;
  if (!/^image\/(png|jpeg|jpg|gif|webp|bmp)$/i.test(file.type)) {
    return toast.error('Allowed types: PNG, JPG, GIF, WEBP, BMP.');
  }
  if (file.size > 2 * 1024 * 1024) return toast.error('File size must be ≤ 2MB.');
  setImageFiles(prev => { const next = [...prev]; next[idx] = file; return next; });
  setFormData(p => {
    const imgs = [...(p.images || [])]; imgs[idx] = '';
    const paths = [...(p.imagePaths || [])]; paths[idx] = '';
    return { ...p, images: imgs, imagePaths: paths };
  });
};


  const handleImageDelete = async (idx) => {
    const path = formData.imagePaths[idx];
    try {
      if (path) {
        const url = apiURL('/api/delete-avatar');
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pathname: path })
        });
        if (!res.ok) throw new Error(await res.text());
      }
      toast.success('Image deleted');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete image');
    } finally {
      setFormData((p) => {
        const imgs = [...(p.images || [])]; imgs[idx] = '';
        const paths = [...(p.imagePaths || [])]; paths[idx] = '';
        return { ...p, images: imgs, imagePaths: paths };
      });
      setImageFiles((prev) => { const n = [...prev]; n[idx] = null; return n; });
      setPreviewUrls((prev) => { const n = [...prev]; n[idx] = ''; return n; });
    }
  };

  const uploadImages = async () => {
  if (!userId) {
    toast.error('User not authenticated.');
    return { urls: [], paths: [] };
  }
  const uploadedUrls = [];
  const uploadedPaths = [];
  for (let i = 0; i < imageFiles.length; i++) {
    const f = imageFiles[i];
    if (!f && formData.images[i]) {
      uploadedUrls[i] = formData.images[i];
      uploadedPaths[i] = formData.imagePaths[i] || '';
      continue;
    }
    if (!f) continue;
    try {
      const ext = extFromMime(f.type);
      if (!ext) throw new Error('Unsupported image type');
      const filename = `forecasts/${userId}/${Date.now()}_${i}${ext}`;
      const url = apiURL(`/api/upload-avatar?filename=${encodeURIComponent(filename)}`);
      const res = await fetch(url, { method: 'POST', body: f });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.json();
      uploadedUrls[i] = blob.url;
      uploadedPaths[i] = filename;
    } catch (e) {
      console.error(e);
      toast.error(`Failed to upload image ${i + 1}`);
    }
  }
  return {
    urls: uploadedUrls.filter(u => typeof u === 'string'),
    paths: uploadedPaths.filter(p => typeof p === 'string'),
  };
};


  // final sanitize & submit
  const buildSanitizedForSave = (raw) => {
    const safeDesc = {
      technicalAnalysis: sanitizeText(raw.description?.technicalAnalysis),
      fundamental: sanitizeText(raw.description?.fundamental),
      news: sanitizeText(raw.description?.news),
      conclusion: sanitizeText(raw.description?.conclusion)
    };
    const safeNews = (raw.newsLinks || []).map(sanitizeURL).filter(Boolean);

    const out = { 
      ...raw, 
      title: sanitizeText(raw.title, 300),
      description: safeDesc 
    }; 
    if (safeNews.length) out.newsLinks = safeNews; 
    if ((raw.images || []).length)     out.images = raw.images; 
    if ((raw.imagePaths || []).length) out.imagePaths = raw.imagePaths; 

    ['likes','views','aiSuggested'].forEach((k) => { if (out[k] === undefined) delete out[k]; }); 
    return out; 
  };

  const handleSubmit = async () => {
for (let i = 0; i <= 4; i++) {
  if (!validateStep(i)) {
    setActiveStep(i);
    return;
  }
}
    

    // convert preset to ISO for moderation
    let dueISO = '';
    if (formData.dueType === 'preset') {
      const preset = migratePreset(formData.duePreset);
      dueISO = durationToISO(preset) || durationToISO('30 days');
    } else {
      // already ISO yyyy-mm-dd from <input type="date">
      dueISO = formData.dueCustom;
    }

    // inject disclaimer if toggle ON and not present
    const textToScan = `${formData.title}\n${Object.values(formData.description || {}).join(' ')}`;
    const nextDescription = { ...formData.description };
    if (riskDisclaimer && !hasRiskDisclosure(textToScan)) {
      nextDescription.conclusion = `${nextDescription.conclusion || ''}\n\n${PLATFORM_DISCLAIMER}`.trim();
    }

    setLoading(true);
    try {
      const { urls, paths } = await Promise.race([
        uploadImages(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Image upload timeout')), 10000))
      ]);

      const raw = {
        userId,
        kind: 'forecast', 
        creatorType: 'user',
        isForecast: true,
        title: formData.title,
        description: nextDescription, // <-- with optional disclaimer
        assets: formData.assets,
        predictionType: formData.predictionType,
        purchasePrice: ['pump', 'dump', 'flat'].includes(formData.predictionType)
          ? formData.purchasePrice
          : undefined,
        targetPrice: ['pump', 'dump', 'flat'].includes(formData.predictionType)
          ? formData.targetPrice
          : undefined,
        due: dueISO, // ISO date
        images: urls,
        imagePaths: paths,
        newsLinks: (formData.newsLinks || []).filter(Boolean),
        status: 'pending',
        views: 0,
        likes: 0,
        aiSuggested: false,
        tipsEnabled: !!formData.tipsEnabled,
        marketStatus: 'open',
        pool: 0,
        createdAt: serverTimestamp()
      };

      const sanitized = buildSanitizedForSave(raw);
      const forecastData = sanitizeForecastData(sanitized);

      const docRef = await Promise.race([
        addDoc(collection(db, 'promos'), forecastData),
        new Promise((_, reject) => setTimeout(() => reject(new Error('addDoc timeout')), 10000))
      ]);
      await getDoc(doc(db, 'promos', docRef.id));

      // clear draft silently (no extra toast)
      resetDraft(false);
      toast.success('Forecast submitted for review!');
      onCreated?.();
    } catch (err) {
      console.error(err);
      if (err?.code === 'permission-denied') {
        toast.error('The publication was rejected by security rules. Check the format of the fields (often it is Empty or invalid newsLinks).');
      } else { 
        toast.error('Error saving forecast: ' + (err?.message || String(err)));
      }
    } finally {
      setLoading(false);
    }
  };

  /* ---------- steps ( → GLASS) ---------- */
  const StepBasics = (
    <Box>
      <TextField
        fullWidth
        label="Forecast Title"
        value={formData.title}
        onChange={(e) => setFD({ title: e.target.value })}
        error={!!fieldErrors.title}
        helperText={fieldErrors.title || 'Enter a catchy title'}
        sx={{
          mb: 2,
          '& .MuiInputBase-root': { color: GLASS.text, background: 'rgba(255,255,255,.02)' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.20)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.35)' },
          '& .MuiFormHelperText-root': { color: GLASS.subtext }
        }}
      />
      <Typography variant="subtitle2" sx={{ mb: 0.5, color: GLASS.text }}>
        Assets
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <FieldHint text="Select one or more assets (type to search)" />
        <Button
          size="small"
          onClick={() => resetDraft(true)}
          sx={{
            textTransform: 'none',
            color: GLASS.text,
            border: '1px solid rgba(255,255,255,.28)',
            borderRadius: 1,
            px: 1,
            '&:hover': { bgcolor: 'rgba(255,255,255,.06)', borderColor: 'rgba(255,255,255,.45)' }
          }}
        >
          Reset draft
        </Button>
      </Stack>
     <Autocomplete
  multiple
  options={assetsList}
  value={formData.assets}
  onChange={(_, v) => setFD({ assets: v })}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Select assets"
      error={!!fieldErrors.assets}
      helperText={fieldErrors.assets}
      sx={{
        '& .MuiInputBase-root': { color: GLASS.text, background: 'rgba(255,255,255,.02)' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.20)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.35)' },
        '& .MuiFormHelperText-root': { color: GLASS.subtext }
      }}
    />
  )}
  renderTags={(value, getTagProps) =>
    value.map((option, index) => {
      const tagProps = getTagProps({ index });
      const { key, ...chipProps } = tagProps;         
      return (
        <Chip
          key={key}
          {...chipProps}
          label={option}
          size="small"
          sx={{
            mr: 0.5, mb: 0.5,
            color: DARK,
            bgcolor: 'rgba(255,255,255,.85)',
            border: '1px solid rgba(255,255,255,.92)',
            '& .MuiChip-label': { px: 0.5, fontWeight: 800 }
          }}
        />
      );
    })
  }
  sx={{
    maxWidth: 520,
    '& .MuiAutocomplete-paper': { bgcolor: '#0f141b', color: GLASS.text }
  }}
/>


      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
        {['BTC', 'ETH', 'BNB', 'SOL'].map((t) => (
          <Chip
            key={t}
            label={t}
            onClick={() =>
              setFD({ assets: [...new Set([...(formData.assets || []), t])] })
            }
            size="small"
            variant="outlined"
            sx={{
              color: GLASS.text,
              border: GLASS.border,
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,.06)' }
            }}
          />
        ))}
      </Stack>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5, color: GLASS.text }}>
          Prediction Type
        </Typography>
        <ToggleButtonGroup
  exclusive
  value={formData.predictionType}
  onChange={(_, v) => v && setFD({ predictionType: v })}
  size="small"
  sx={{
    '& .MuiToggleButton-root': {
      color: GLASS.text,
      borderColor: 'rgba(255,255,255,.22)',
    },
    '& .MuiToggleButton-root.Mui-selected': {
      color: DARK,
      backgroundColor: 'rgba(255,255,255,.90)',
      borderColor: 'rgba(255,255,255,.92)',
    },
    '& .MuiToggleButton-root.Mui-selected:hover': {
      backgroundColor: '#ffffff',
    }
  }}
>

          {predictionTypes.map((p) => (
            <ToggleButton key={p.v} value={p.v}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );

  const StepPricing = (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Purchase Price (e.g. 100-120$)"
          value={formData.purchasePrice}
          onChange={(e) => setFD({ purchasePrice: e.target.value })}
          error={!!fieldErrors.purchasePrice}
          helperText={
            ['pump', 'dump', 'flat'].includes(formData.predictionType)
              ? fieldErrors.purchasePrice || 'Format: X-Y$'
              : 'Disabled for this type'
          }
          disabled={!['pump', 'dump', 'flat'].includes(formData.predictionType)}
          sx={{
            '& .MuiInputBase-root': { color: GLASS.text, background: 'rgba(255,255,255,.02)' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.20)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.35)' },
            '& .MuiFormHelperText-root': { color: GLASS.subtext }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Target Price (e.g. 100-1500$)"
          value={formData.targetPrice}
          onChange={(e) => setFD({ targetPrice: e.target.value })}
          error={!!fieldErrors.targetPrice}
          helperText={
            ['pump', 'dump', 'flat'].includes(formData.predictionType)
              ? fieldErrors.targetPrice || 'Format: X-Y$'
              : 'Disabled for this type'
          }
          disabled={!['pump', 'dump', 'flat'].includes(formData.predictionType)}
          sx={{
            '& .MuiInputBase-root': { color: GLASS.text, background: 'rgba(255,255,255,.02)' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.20)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.35)' },
            '& .MuiFormHelperText-root': { color: GLASS.subtext }
          }}
        />
      </Grid>
    </Grid>
  );

  const StepDeadline = (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: R12,
        background: GLASS.surface,
        border: GLASS.border,
        boxShadow: GLASS.shadow,
        color: GLASS.text
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <CalendarMonthIcon sx={{ color: GLASS.text, opacity: .9 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GLASS.text }}>
          Deadline
        </Typography>
        <Chip
          size="small"
          sx={{
            ml: 'auto',
            bgcolor: 'rgba(255,255,255,.06)',
            border: GLASS.border,
            color: GLASS.text,
            '& .MuiChip-label': { px: 0.75 }
          }}
          label={formData.dueType === 'preset' ? formData.duePreset : formData.dueCustom || '—'}
        />
      </Stack>

      <ToggleButtonGroup
  exclusive
  value={formData.dueType}
  onChange={(_, v) => v && setFD({ dueType: v })}
  size="small"
  sx={{
    '& .MuiToggleButton-root': {
      color: GLASS.text,
      borderColor: 'rgba(255,255,255,.22)',
    },
    '& .MuiToggleButton-root.Mui-selected': {
      color: DARK,
      backgroundColor: 'rgba(255,255,255,.90)',
      borderColor: 'rgba(255,255,255,.92)',
    },
    '& .MuiToggleButton-root.Mui-selected:hover': {
      backgroundColor: '#ffffff',
    }
  }}
>

        <ToggleButton value="preset">Preset</ToggleButton>
        <ToggleButton value="custom">Custom</ToggleButton>
      </ToggleButtonGroup>

      {formData.dueType === 'preset' ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
          {duePresets.map((p) => {
            const selected = formData.duePreset === p;
            return (
              <Chip
                key={p}
                label={p}
                onClick={() => setFD({ duePreset: p })}
                size="small"
                variant={selected ? 'filled' : 'outlined'}
                sx={{
                  mb: 1,
                  color: GLASS.text,
                  border: GLASS.border,
                  bgcolor: selected ? 'rgba(255,255,255,.10)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,.12)' }
                }}
              />
            );
          })}
        </Stack>
      ) : (
        <TextField
          fullWidth
          type="date"
          label="Custom date"
          value={formData.dueCustom}
          onChange={(e) => setFD({ dueCustom: e.target.value })}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
          error={!!fieldErrors.due}
          helperText={fieldErrors.due || 'Pick a future date'}
          sx={{
            mt: 1,
            '& .MuiInputBase-root': { color: GLASS.text, background: 'rgba(255,255,255,.02)' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.20)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.35)' },
            '& .MuiFormHelperText-root': { color: GLASS.subtext }
          }}
        />
      )}

      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: GLASS.subtext }}>
        Preset will be converted to an ISO date on submit.
      </Typography>
    </Paper>
  );

  const descHints = {
    technicalAnalysis:
      'Trend, support/resistance zones, indicators (MACD/RSI/Volume/Fibonacci), funding.',
    fundamental:
      'Dominance, on-chain metrics, market cap, inflows/outflows, fees, active addresses.',
    news: 'Links and short summary from reliable sources.',
    conclusion: 'Final view: thesis, risks, conviction.'
  };

  const StepDescription = (
    <Stack spacing={1.5}>
      <DescField
        label="Technical Analysis"
        name="description.technicalAnalysis"
        value={formData.description.technicalAnalysis}
        onChange={(e) =>
          setFD({ description: { ...formData.description, technicalAnalysis: e.target.value } })
        }
        hint={descHints.technicalAnalysis}
        errorText={fieldErrors['d.technicalAnalysis']}
      />
      <DescField
        label="Fundamental Analysis"
        name="description.fundamental"
        value={formData.description.fundamental}
        onChange={(e) =>
          setFD({ description: { ...formData.description, fundamental: e.target.value } })
        }
        hint={descHints.fundamental}
        errorText={fieldErrors['d.fundamental']}
      />
      <DescField
        label="News"
        name="description.news"
        value={formData.description.news}
        onChange={(e) =>
          setFD({ description: { ...formData.description, news: e.target.value } })
        }
        hint={descHints.news}
        errorText={fieldErrors['d.news']}
      />
      <DescField
        label="Conclusion"
        name="description.conclusion"
        value={formData.description.conclusion}
        onChange={(e) =>
          setFD({ description: { ...formData.description, conclusion: e.target.value } })
        }
        hint={descHints.conclusion}
        errorText={fieldErrors['d.conclusion']}
      />
    </Stack>
  );

  const StepMedia = (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: R12,
          borderStyle: 'dashed',
          borderColor: 'rgba(255,255,255,.24)',
          background: GLASS.surface,
          color: GLASS.text,
          mb: 2,
          boxShadow: GLASS.shadow
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <ImageIcon sx={{ color: GLASS.text, opacity: .9 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GLASS.text }}>
            Images
          </Typography>
          <Typography variant="caption" sx={{ color: GLASS.subtext }}>
            (up to 2)
          </Typography>
        </Stack>

        <Grid container spacing={1.5}>
          {[0, 1].map((i) => {
            const remote = formData.images?.[i] || '';
            const localPreview = previewUrls[i] || '';
            const hasImage = isSafeImageURL(remote) || !!localPreview;
            const safeSrc = hasImage
              ? (isSafeImageURL(remote) ? remote : localPreview) || EMPTY_IMG
              : EMPTY_IMG;

            return (
              <Grid item xs={12} sm={6} key={i}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 140,
                    border: '1px dashed rgba(255,255,255,.24)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,.02)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {hasImage ? (
                    <>
                      <img
                        src={safeSrc}
                        alt={`Preview ${i + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        loading="lazy"
                      />
                      {(remote || imageFiles[i]) && (
                        <IconButton
                          size="small"
                          onClick={() => handleImageDelete(i)}
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            color: '#fff',
                            bgcolor: 'rgba(0,0,0,.55)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,.7)' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  ) : (
                    <Button
                      variant="text"
                      component="label"
                      sx={{ textTransform: 'none', color: GLASS.text }}
                      startIcon={<AddPhotoAlternateIcon />}
                    >
                      Upload
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/bmp"
                        hidden
                        onChange={(e) => handleImagePick(e, i)}
                      />
                    </Button>
                  )}
                </Box>

                {imageFiles[i] && !formData.images[i] && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, display: 'block', color: GLASS.subtext }}
                  >
                    {imageFiles[i].name}
                  </Typography>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <Typography variant="subtitle2" sx={{ mb: 1, color: GLASS.text }}>
        News Links (up to 2)
      </Typography>
      <Grid container spacing={2}>
        {[0, 1].map((i) => (
          <Grid item xs={12} md={6} key={i}>
            <TextField
              fullWidth
              label={`News Link ${i + 1}`}
              value={formData.newsLinks[i] || ''}
              onChange={(e) => {
                const nl = [...formData.newsLinks];
                nl[i] = e.target.value;
                setFD({ newsLinks: nl });
              }}
              error={!!fieldErrors.newsLinks}
              helperText={i === 0 ? fieldErrors.newsLinks || 'Optional' : 'Optional'}
              sx={{
                '& .MuiInputBase-root': { color: GLASS.text, background: 'rgba(255,255,255,.02)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.20)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.35)' },
                '& .MuiFormHelperText-root': { color: GLASS.subtext }
              }}
            />
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Tips</Typography>
        <Chip
          label={formData.tipsEnabled ? 'Enabled' : 'Disabled'}
          variant={formData.tipsEnabled ? 'filled' : 'outlined'}
          sx={{
            cursor: 'pointer',
            color: GLASS.text,
            border: GLASS.border,
            bgcolor: formData.tipsEnabled ? 'rgba(255,255,255,.10)' : 'transparent'
          }}
          onClick={() => setFD({ tipsEnabled: !formData.tipsEnabled })}
        />
      </Stack>

      {/* NEW: risk disclaimer toggle */}
      <FormControlLabel
        sx={{ mt: 1, color: GLASS.text }}
        control={
          <Checkbox
            checked={riskDisclaimer}
            onChange={(e) => setRiskDisclaimer(e.target.checked)}
            sx={{ color: GLASS.subtext, '&.Mui-checked': { color: GLASS.green } }}
          />
        }
        label="Add risk disclaimer"
      />
    </Box>
  );

  const StepReview = (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, color: GLASS.text }}>
        Review your forecast
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: R12,
          background: GLASS.surface,
          border: GLASS.border,
          boxShadow: GLASS.shadow,
          color: GLASS.text
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Title</Typography>
            <Typography sx={{ mb: 1, color: GLASS.subtext }}>{formData.title || '—'}</Typography>

            <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Assets</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
              {formData.assets.map((a) => (
                <Chip key={a} label={a} size="small" sx={{ color: GLASS.text, border: GLASS.border }} variant="outlined" />
              ))}
            </Stack>

            <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Prediction</Typography>
            <Typography sx={{ mb: 1, color: GLASS.subtext }}>{formData.predictionType}</Typography>

            {['pump', 'dump', 'flat'].includes(formData.predictionType) && (
              <>
                <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Purchase / Target</Typography>
                <Typography sx={{ mb: 1, color: GLASS.subtext }}>
                  {formData.purchasePrice || '—'} / {formData.targetPrice || '—'}
                </Typography>
              </>
            )}

            <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Deadline</Typography>
            <Typography sx={{ mb: 1, color: GLASS.subtext }}>
              {formData.dueType === 'preset' ? formData.duePreset : formData.dueCustom || '—'}
            </Typography>

            <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,.12)' }} />

            <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Technical Analysis</Typography>
            <Typography sx={{ mb: 1, whiteSpace: 'pre-wrap', color: GLASS.subtext }}>
              {formData.description.technicalAnalysis || '—'}
            </Typography>

            <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Fundamental Analysis</Typography>
            <Typography sx={{ mb: 1, whiteSpace: 'pre-wrap', color: GLASS.subtext }}>
              {formData.description.fundamental || '—'}
            </Typography>

            <Typography variant="subtitle2" sx={{ color: GLASS.text }}>News</Typography>
            <Typography sx={{ mb: 1, whiteSpace: 'pre-wrap', color: GLASS.subtext }}>
              {formData.description.news || '—'}
            </Typography>

            <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Conclusion</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap', color: GLASS.subtext }}>
              {formData.description.conclusion || '—'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ color: GLASS.text }}>Images</Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {[0, 1].map((i) => {
                const remote = formData.images?.[i] || '';
                const localPreview = previewUrls[i] || '';
                const hasImage = isSafeImageURL(remote) || !!localPreview;
                const safeSrc = hasImage
                  ? (isSafeImageURL(remote) ? remote : localPreview) || EMPTY_IMG
                  : EMPTY_IMG;
                return (
                  <Box
                    key={i}
                    sx={{
                      height: 100,
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      border: GLASS.border,
                      bgcolor: 'rgba(255,255,255,.02)'
                    }}
                  >
                    <img
                      src={safeSrc}
                      alt={`Preview ${i + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      loading="lazy"
                    />
                  </Box>
                );
              })}
            </Stack>

            <Typography variant="subtitle2" sx={{ mt: 2, color: GLASS.text }}>
              News Links
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {(formData.newsLinks || []).filter(Boolean).map((l, idx) => (
                <Typography key={idx} variant="body2" sx={{ wordBreak: 'break-all', color: GLASS.subtext }}>
                  {l}
                </Typography>
              ))}
              {!(formData.newsLinks || []).filter(Boolean).length && <Typography sx={{ color: GLASS.subtext }}>—</Typography>}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', p: { xs: 1, sm: 2 } }}>
      {/* sticky progress (glass) */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: 'rgba(8,11,17,.65)',
          backdropFilter: 'blur(8px)',
          borderBottom: GLASS.border,
          pt: 1,
          pb: 1.25
        }}
      >
        <Stepper
  activeStep={activeStep}
  alternativeLabel
  sx={{
    '& .MuiStepIcon-root': { color: 'rgba(255,255,255,.28)' }, 
    '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
      color: GLASS.text, 
    },
    '& .MuiStepIcon-text': { fill: DARK, fontWeight: 800 }, 
    '& .MuiStepLabel-label': { color: GLASS.subtext },
    '& .Mui-active .MuiStepLabel-label, & .Mui-completed .MuiStepLabel-label': { color: GLASS.text }
  }}
>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* step header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: "'Bruno Ace SC', sans-serif", color: GLASS.text }}>
          {steps[activeStep]}
        </Typography>
        <Tooltip title="Let AI help with this step">
          <span>
            <Button
              size="small"
              startIcon={<SmartToyIcon />}
              disabled={loading}
              onClick={applyAIStep}
              sx={{
                ml: 1,
                textTransform: 'none',
                color: GLASS.text,
                border: '1px solid rgba(255,255,255,.28)',
                borderRadius: 1,
                px: 1.25,
                '&:hover': { bgcolor: 'rgba(255,255,255,.06)', borderColor: 'rgba(255,255,255,.45)' }
              }}
            >
              AI Suggest
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* step content */}
      {activeStep === 0 && StepBasics}
      {activeStep === 1 && StepPricing}
      {activeStep === 2 && StepDeadline}
      {activeStep === 3 && StepDescription}
      {activeStep === 4 && StepMedia}
      {activeStep === 5 && StepReview}

      {/* nav */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          onClick={back}
          disabled={activeStep === 0 || loading}
          sx={{
            textTransform: 'none',
            color: GLASS.text,
            borderColor: 'rgba(255,255,255,.32)',
            '&:hover': { borderColor: 'rgba(255,255,255,.5)', bgcolor: 'rgba(255,255,255,.06)' }
          }}
        >
          Back
        </Button>

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={next}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              background: 'linear-gradient(90deg,#2d74d7 0%,#14F195 100%)'
            }}
          >
            Next
          </Button>
        ) : (

          <Tooltip title="Coming soon to Step V2">
    <span>
      <Button
    variant="contained"
    onClick={handleSubmit}
    disabled={loading}
    sx={{
      textTransform: 'none',
      fontWeight: 800,
      background: 'linear-gradient(90deg,#2d74d7 0%,#14F195 100%)',
    }}
  >
    Publish forecast
  </Button>
    </span>
  </Tooltip>
)}
          

        {editData && (
          <Button
            variant="text"
            onClick={onCloseEdit}
            disabled={loading}
            sx={{ textTransform: 'none', color: GLASS.subtext }}
          >
            Cancel
          </Button>
        )}
      </Stack>

      <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,.12)' }} />

      <Typography variant="caption" sx={{ color: GLASS.subtext }}>
        Draft is saved automatically. You can reset it in Basics → “Reset draft”.
      </Typography>
    </Box>
  );
}